/**
 * offlineQueue.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Browser-side holding area for CAR-T Playbook changes that could not reach the
 * server yet (offline / server unavailable). Used by useAutosave.
 *
 * PHI NOTE: an entry holds unsaved clinical form fields, so it is kept only as
 * long as it has to be:
 *   - keyed by user AND case, and only ever replayed for the same user
 *   - deleted as soon as the change is saved
 *   - dropped after MAX_AGE_MS (stale entries are never replayed)
 *   - the whole store is wiped on explicit logout (clearOfflineQueue), and
 *     nothing is written while signed out
 * An entry written before a session expired survives, so the same user can sign
 * back in and have the change saved.
 *
 * Every function is best-effort and never throws - if IndexedDB is unavailable
 * (private mode, blocked storage) the queue is simply empty.
 */

import { getAccessToken } from './auth';

const DB_NAME = 'cart_playbook';
const STORE = 'autosave_queue_v2';
const DB_VERSION = 2;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // v1 stored entries keyed by caseId only (not tied to a user) - drop it.
      if (db.objectStoreNames.contains('autosave_queue')) db.deleteObjectStore('autosave_queue');
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function entryKey(userId, caseId) {
  return `${userId}:${caseId}`;
}

async function withStore(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const result = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(result && 'result' in result ? result.result : undefined);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Store (replace) the unsaved payload for this user + case. Never while signed
 *  out: a save that fails after logout must not write clinical data back. */
export async function putQueued(userId, caseId, data) {
  if (userId == null || caseId == null || !getAccessToken()) return;
  try {
    await withStore('readwrite', (s) => s.put({ key: entryKey(userId, caseId), userId, caseId, data, queuedAt: Date.now() }));
  } catch (_) { /* best-effort */ }
}

/** The unsaved payload for this user + case, or null. Expired entries are removed. */
export async function getQueued(userId, caseId) {
  if (userId == null || caseId == null) return null;
  try {
    const entry = await withStore('readonly', (s) => s.get(entryKey(userId, caseId)));
    if (!entry || entry.userId !== userId) return null;
    if (Date.now() - (entry.queuedAt || 0) > MAX_AGE_MS) {
      await deleteQueued(userId, caseId);
      return null;
    }
    return entry;
  } catch (_) {
    return null;
  }
}

export async function deleteQueued(userId, caseId) {
  if (userId == null || caseId == null) return;
  try {
    await withStore('readwrite', (s) => s.delete(entryKey(userId, caseId)));
  } catch (_) { /* best-effort */ }
}

/** Wipe every queued change on this device (explicit logout). */
export function clearOfflineQueue() {
  try {
    if (typeof indexedDB !== 'undefined') indexedDB.deleteDatabase(DB_NAME);
  } catch (_) { /* best-effort */ }
}
